import { memo, useEffect, useState } from 'react';
import { Box, CircularProgress, List, ListItem, Typography } from '@mui/material';
import { useCatalogService } from '../services/catalogService';
import type { DeploymentHistory } from '../../../types/backend';

interface DeploymentRowHistoryProps {
  customerId: string;
  deploymentId: string;
  active: boolean;
}

const DeploymentRowHistory = memo<DeploymentRowHistoryProps>(({
  customerId,
  deploymentId,
  active,
}) => {
  const catalogService = useCatalogService();
  const [history, setHistory] = useState<DeploymentHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    catalogService
      .getDeploymentHistory(customerId, deploymentId)
      .then((result) => {
        if (mounted) setHistory(result.history);
      })
      .catch((err) => {
        if (mounted) {
          setHistory([]);
          setError(err instanceof Error ? err.message : 'Failed to load history');
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [active, catalogService, customerId, deploymentId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error" sx={{ py: 2 }}>{error}</Typography>;
  }

  if (history.length === 0) {
    return <Typography color="text.secondary" sx={{ py: 2 }}>No history available.</Typography>;
  }

  return (
    <List dense disablePadding sx={{ py: 1 }}>
      {history.map((entry) => (
        <ListItem key={entry.event_id} disablePadding sx={{ py: 0.5, flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {entry.event_type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(entry.timestamp).toLocaleString()}
            {entry.actor && ` · ${entry.actor}`}
          </Typography>
          {entry.error_detail && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
              {entry.error_detail}
            </Typography>
          )}
        </ListItem>
      ))}
    </List>
  );
});

DeploymentRowHistory.displayName = 'DeploymentRowHistory';
export default DeploymentRowHistory;
